import { JsonApiRequestError } from '@farfetched/core';
import { ComboboxItem } from '@mantine/core';
import { sample, Store } from 'effector';
import { debounce } from 'patronum';
import {
	BetReport,
	BetReportAdditionalData,
	BetReportRegistryFilters,
	createBetReportRegistryQuery,
	createGetCashdeskAllQuery,
	createGetGamesQuery,
} from 'shared/api';
import {
	createDateFilterModel,
	createRegistryModel,
	DateFilterModel,
	getToday,
	RegistryModel,
} from 'shared/lib';

export type BetReportRegistryModel = {
	registryModel: RegistryModel<
		BetReport,
		BetReportRegistryFilters,
		JsonApiRequestError,
		BetReportAdditionalData
	>;
	dateFilterModel: DateFilterModel;
	$cashdesks: Store<ComboboxItem[]>;
	$games: Store<ComboboxItem[]>;
	$total: Store<BetReportAdditionalData['summary'] | null>;
	$isMultiCurrencies: Store<boolean>;
};

export const createBetReportRegistryModel = (): BetReportRegistryModel => {
	const query = createBetReportRegistryQuery();

	const [todayDate, currentTimeZone] = getToday();

	const getCashdeskAllQuery = createGetCashdeskAllQuery();

	const getGamesQuery = createGetGamesQuery();

	const registryModel = createRegistryModel<
		BetReport,
		BetReportRegistryFilters,
		JsonApiRequestError,
		BetReportAdditionalData
	>({
		query,
		filters: {
			currencies: [],
			accountIds: [],
			cashdeskIds: [],
			gameIds: [],
			dateFrom: todayDate,
			dateTo: todayDate,
			timeZone: currentTimeZone,
		},
	});

	const dateFilterModel = createDateFilterModel({
		fromChange: registryModel.filters.dateFrom.setValue,
		toChange: registryModel.filters.dateTo.setValue,
	});

	const $cashdesks = getCashdeskAllQuery.$data.map(
		(cashdesks) => cashdesks?.map(({ id, name }) => ({ value: `${id}`, label: `${name}` })) ?? [],
	);

	const $games = getGamesQuery.$data.map(
		(games) => games?.map(({ id, title }) => ({ value: `${id}`, label: `${title}` })) ?? [],
	);

	const $total = registryModel.$additionalData.map((data) => data?.summary ?? null);

	const $isMultiCurrencies = $total.map((total) => !total?.currency);

	sample({
		clock: debounce(registryModel.filters.accountIds.$value, 300),
		filter: registryModel.gate.status,
		target: getCashdeskAllQuery.start,
	});

	sample({
		clock: getCashdeskAllQuery.finished.success,
		source: { all: $cashdesks, current: registryModel.filters.cashdeskIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((cashdesk) => current.includes(Number(cashdesk.value)))
				.map((cashdesk) => Number(cashdesk.value)),
		target: registryModel.filters.cashdeskIds.setValue,
	});

	sample({
		clock: registryModel.gate.open,
		source: registryModel.filters.accountIds.$value,
		target: [getCashdeskAllQuery.start, getGamesQuery.start],
	});

	sample({
		clock: registryModel.gate.close,
		target: [getCashdeskAllQuery.reset, getGamesQuery.reset],
	});

	return {
		registryModel,
		dateFilterModel,
		$cashdesks,
		$games,
		$total,
		$isMultiCurrencies,
	};
};
